import type { Action } from '../common/actions'
import {
    CommentDeletionStarted,
    CommentWasDeleted,
    DeleteCommentCanceled,
    DeleteCommentClicked,
    DeleteCommentFailed,
    EditCommentCanceled,
    EditCommentClicked,
    EditCommentFailed,
    EditCommentSuccessful,
    EditedCommentSubmitted,
    FileWasCommented,
} from '../files/actions'

export enum UserActionStatus {
    PENDING_USER__EDIT = 'PENDING_USER__EDIT', // User updates text
    PENDING_SERVER__EDIT = 'PENDING_SERVER__EDIT', // Edited text submitted by user, waiting for server
    SERVER_ERROR__EDIT = 'SERVER_ERROR__EDIT', // Something went wrong when trying to save comment
    PENDING_USER__DELETE = 'PENDING_USER__DELETE', // Awaits user decision
    PENDING_SERVER__DELETE = 'PENDING_SERVER__DELETE', // Waiting for server to delete comment
    SERVER_ERROR__DELETE = 'SERVER_ERROR__DELETE', // Something went wrong when trying to delete comment
}

export interface FileComment {
    fileID: FileID
    commentUUID: CommentID
    comment: string
    timestamp: number
    userUUID: string
    userActionStatus?: UserActionStatus
    originalComment?: string
    lastEditTimestamp?: number
}

export type CommentsState = DictionaryOf<FileComment>

const initialState: CommentsState = {}

const newStateWithElementChanges = (
    state: CommentsState,
    elementId: CommentID,
    changes: Partial<FileComment>,
): CommentsState => {
    const element: FileComment = {
        ...state[elementId],
        ...changes,
    }

    return {
        ...state,
        [elementId]: element,
    }
}

export const commentsReducer = (
    state: CommentsState = initialState,
    action: Action,
): CommentsState => {
    if (FileWasCommented.match(action)) {
        // Backend emits same action-type for new comments as for edits. Only able to detect by inspecting state
        const oldComment = state[action.payload.commentUUID]
        const isExistingComment = oldComment !== undefined
        if (isExistingComment && oldComment.comment !== action.payload.comment) {
            // Only mark as edit when text updates
            // (as identical actions are provided from UI and from backend when commenting)
            return newStateWithElementChanges(state, action.payload.commentUUID, {
                comment: action.payload.comment,
                lastEditTimestamp: action.payload.timestamp,
            })
        }
        if (!isExistingComment) {
            return newStateWithElementChanges(state, action.payload.commentUUID, action.payload)
        }
        return state
    }

    if (CommentWasDeleted.match(action)) {
        const { [action.payload]: _omit, ...comments } = state
        return comments
    }

    if (DeleteCommentClicked.match(action)) {
        if (state[action.payload.commentID] !== undefined) {
            return newStateWithElementChanges(state, action.payload.commentID, {
                userActionStatus: UserActionStatus.PENDING_USER__DELETE,
            })
        }
    }

    if (CommentDeletionStarted.match(action)) {
        if (state[action.payload.commentID] !== undefined) {
            return newStateWithElementChanges(state, action.payload.commentID, {
                userActionStatus: UserActionStatus.PENDING_SERVER__DELETE,
            })
        }
    }

    if (DeleteCommentCanceled.match(action)) {
        if (state[action.payload.commentID] !== undefined) {
            return newStateWithElementChanges(state, action.payload.commentID, {
                userActionStatus: undefined,
            })
        }
    }

    if (DeleteCommentFailed.match(action)) {
        if (state[action.payload.commentID] !== undefined) {
            return newStateWithElementChanges(state, action.payload.commentID, {
                userActionStatus: UserActionStatus.SERVER_ERROR__DELETE,
            })
        }
    }

    if (EditCommentClicked.match(action)) {
        const oldComment = state[action.payload.commentID]
        if (oldComment !== undefined) {
            return newStateWithElementChanges(state, action.payload.commentID, {
                userActionStatus: UserActionStatus.PENDING_USER__EDIT,
                originalComment: oldComment.comment,
            })
        }
    }

    if (EditCommentCanceled.match(action)) {
        const oldComment = state[action.payload.commentID]
        if (oldComment !== undefined) {
            return newStateWithElementChanges(state, action.payload.commentID, {
                userActionStatus: undefined,
                originalComment: oldComment.originalComment,
            })
        }
    }

    if (EditedCommentSubmitted.match(action)) {
        if (state[action.payload.commentID] !== undefined) {
            return newStateWithElementChanges(state, action.payload.commentID, {
                comment: action.payload.text,
                userActionStatus: UserActionStatus.PENDING_SERVER__EDIT,
            })
        }
    }

    if (EditCommentSuccessful.match(action)) {
        if (state[action.payload.commentID] !== undefined) {
            return newStateWithElementChanges(state, action.payload.commentID, {
                userActionStatus: undefined,
                lastEditTimestamp: action.payload.timestamp,
            })
        }
    }

    if (EditCommentFailed.match(action)) {
        const oldComment = state[action.payload.commentID]
        if (oldComment !== undefined) {
            return newStateWithElementChanges(state, action.payload.commentID, {
                userActionStatus: UserActionStatus.SERVER_ERROR__EDIT,
                comment: oldComment.originalComment,
            })
        }
    }

    return state
}

export const commentsReducerMapObj = {
    comments: commentsReducer,
}

export type StateWithComments = StateOfReducerMapObj<typeof commentsReducerMapObj>
